import { SigninRedirectArgs } from 'oidc-client-ts';
import { Environment, LogisticsTenant } from '../../common/types';
import { neverReachedFor } from '../../utils';
import { asBool, EnvironmentSpecificConfig, LoginConfig } from '../config';

export const resolveEnvironment = (hostname: string): Environment => {
    if (hostname.startsWith(Environment.VW_QA)) {
        return Environment.VW_QA;
    }
    if (hostname.startsWith(Environment.VW)) {
        return Environment.VW;
    }
    return Environment.RIO;
};

export const environmentForLogisticsTenant = (logisticsTenant: LogisticsTenant | undefined): Environment => {
    switch (logisticsTenant) {
        case LogisticsTenant.RIO:
            return Environment.RIO;
        case LogisticsTenant.VW_QA:
            return Environment.VW_QA;
        case LogisticsTenant.VW:
            return Environment.VW;
        case undefined:
            return Environment.RIO; // For PowerCO
        default:
            return neverReachedFor(logisticsTenant);
    }
};

export const logisticsTenantMatchesEnvironment = (
    logisticsTenant: LogisticsTenant | undefined,
    environment: Environment | undefined,
): boolean => environmentForLogisticsTenant(logisticsTenant) === environment;

export const createEnvironmentSpecificConfig = (environment: Environment): EnvironmentSpecificConfig => {
    switch (environment) {
        case Environment.RIO:
            return rioEnvironmentConfig();
        case Environment.VW_QA:
            return vwQaEnvironmentConfig();
        case Environment.VW:
            return vwEnvironmentConfig();
        default:
            return neverReachedFor(environment);
    }
};

export const createLoginConfig = (environment: Environment): LoginConfig => {
    switch (environment) {
        case Environment.RIO:
            return rioLoginConfig();
        case Environment.VW_QA:
            return {
                ...rioLoginConfig(),
                redirectUri: import.meta.env.VITE_LOGIN_REDIRECT_URI_VW_QA,
                silentRedirectUri: import.meta.env.VITE_LOGIN_SILENT_REDIRECT_URI_VW_QA,
                postLogoutRedirectUri: import.meta.env.VITE_POST_LOGOUT_REDIRECT_URI_VW_QA,
            };
        case Environment.VW:
            return {
                ...rioLoginConfig(),
                redirectUri: import.meta.env.VITE_LOGIN_REDIRECT_URI_VW,
                silentRedirectUri: import.meta.env.VITE_LOGIN_SILENT_REDIRECT_URI_VW,
                postLogoutRedirectUri: import.meta.env.VITE_POST_LOGOUT_REDIRECT_URI_VW,
            };
        default:
            return neverReachedFor(environment);
    }
};

export const getSigninOptions = (environment: Environment): SigninRedirectArgs => {
    switch (environment) {
        case Environment.VW_QA:
            return { extraQueryParams: { idp: 'cloud-vwgroup-com-qa' } };
        case Environment.VW:
            return { extraQueryParams: { idp: 'cloud-vwgroup-com' } };
        case Environment.RIO:
            return {};
        default:
            return neverReachedFor(environment);
    }
};

const rioLoginConfig = (): LoginConfig => ({
    authority: import.meta.env.VITE_LOGIN_AUTHORITY,
    clientId: import.meta.env.VITE_CLIENT_ID,
    redirectUri: import.meta.env.VITE_LOGIN_REDIRECT_URI,
    silentRedirectUri: import.meta.env.VITE_LOGIN_SILENT_REDIRECT_URI,
    postLogoutRedirectUri: import.meta.env.VITE_POST_LOGOUT_REDIRECT_URI,
    oauthScope: [
        'openid',
        'profile',
        'email',
        'webedi-notification-settings.read',
        'webedi-notification-settings.write',
    ],
    monitorSession: false,
    preventRedirect: asBool(import.meta.env.VITE_LOGIN_PREVENT_REDIRECT),
});

const rioEnvironmentConfig = (): EnvironmentSpecificConfig => ({
    brand: 'rio',
    closingDaysUrl: import.meta.env.VITE_CLOSING_DAYS_FRONTEND_RIO,
    htmlTitle: import.meta.env.VITE_RIO_HTML_TITLE,
    despatchUrl: import.meta.env.VITE_DESPATCH_FRONTEND_RIO,
    executionLogUrl: import.meta.env.VITE_EXECUTION_LOG_FRONTEND_RIO,
});

const vwQaEnvironmentConfig = (): EnvironmentSpecificConfig => ({
    closingDaysUrl: import.meta.env.VITE_CLOSING_DAYS_FRONTEND_VW_QA,
    htmlTitle: import.meta.env.VITE_VW_HTML_TITLE,
    brand: 'vw',
    despatchUrl: import.meta.env.VITE_DESPATCH_FRONTEND_VW_QA,
    executionLogUrl: import.meta.env.VITE_EXECUTION_LOG_FRONTEND_VW_QA,
});

const vwEnvironmentConfig = (): EnvironmentSpecificConfig => ({
    closingDaysUrl: import.meta.env.VITE_CLOSING_DAYS_FRONTEND_VW,
    htmlTitle: import.meta.env.VITE_VW_HTML_TITLE,
    brand: 'vw',
    despatchUrl: import.meta.env.VITE_DESPATCH_FRONTEND_VW,
    executionLogUrl: import.meta.env.VITE_EXECUTION_LOG_FRONTEND_VW,
});
